import type { TodoListOption } from '@agentic-flow-studio/shared/types/todo-sink';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@agentic-flow-studio/shared/ui/card';
import { toast } from 'sonner';
import { trpc } from '@/utils/trpc';

/**
 * Where a member's tasks are mirrored.
 *
 * Personal rather than workspace configuration: a Microsoft To Do list lives in one
 * person's mailbox, so this shows the signed-in member their own lists and never
 * anybody else's. Having no destination is the default and stays permanently valid.
 */
/** The viewer's own IANA zone, or null where the browser will not say. */
function browserTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

export function TodoSinkSettings() {
  const utils = trpc.useUtils();
  const sinkQuery = trpc.todoSink.get.useQuery();
  const listsQuery = trpc.todoSink.listLists.useQuery();

  const nominate = trpc.todoSink.nominate.useMutation({
    onSuccess: (sink) => {
      toast.success(
        sink ? `Tasks will appear in ${sink.listDisplayName ?? 'your list'}` : 'Destination saved'
      );
      utils.todoSink.invalidate();
    },
    onError: (error) => toast.error(error.message),
  });

  const remove = trpc.todoSink.remove.useMutation({
    onSuccess: () => {
      toast.success('Tasks stay in Inbox Clinic from now on');
      utils.todoSink.invalidate();
    },
    onError: (error) => toast.error(error.message),
  });

  const sink = sinkQuery.data ?? null;
  const lists = listsQuery.data;
  const isBusy = nominate.isPending || remove.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Microsoft To Do</CardTitle>
        <CardDescription>
          Tasks you create here can also appear in one of your own To Do lists, so you can work from
          where you already do. Completing a task in either place settles it in both.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {sinkQuery.isLoading || listsQuery.isLoading ? (
          <p className="text-sm text-murmur">Checking your Microsoft account.</p>
        ) : null}

        {lists && !lists.available ? <Unavailable reason={lists.reason} /> : null}

        {lists?.available ? (
          <>
            <CurrentDestination
              listDisplayName={sink?.listDisplayName ?? null}
              accountEmail={lists.accountEmail}
            />

            <div>
              <h3 className="text-sm text-ink mb-1">Your lists</h3>
              <p className="text-sm text-murmur mb-3 max-w-measure">
                Pick the one you actually look at. You can change it whenever, and changing it
                leaves everything already sent where it is.
              </p>

              <ul>
                {lists.lists.map((list) => (
                  <ListRow
                    key={list.id}
                    list={list}
                    isCurrent={sink?.listId === list.id}
                    isBusy={isBusy}
                    onChoose={() =>
                      nominate.mutate({
                        accountId: lists.accountId,
                        listId: list.id,
                        listDisplayName: list.displayName,
                        // A personal Microsoft account has no mailbox time zone to
                        // report, so this is the only way a due date lands on the
                        // day you actually picked.
                        timeZone: browserTimeZone(),
                      })
                    }
                  />
                ))}
              </ul>
            </div>

            {sink ? (
              <div className="pt-2">
                <Button
                  variant="ghost"
                  disabled={isBusy}
                  onClick={() => remove.mutate({})}
                  className="px-0"
                >
                  Stop sending tasks to To Do
                </Button>
                <p className="text-sm text-murmur mt-1 max-w-measure">
                  Tasks already in your list stay there. Nothing is removed from Microsoft.
                </p>
              </div>
            ) : null}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

function CurrentDestination({
  listDisplayName,
  accountEmail,
}: {
  listDisplayName: string | null;
  accountEmail: string | null;
}) {
  if (!listDisplayName) {
    return (
      <p className="text-sm text-murmur max-w-measure">
        Nothing is being sent to To Do at the moment. Tasks live here until you choose a list.
      </p>
    );
  }

  return (
    <p className="text-sm text-ink max-w-measure">
      Tasks are going to <span className="font-display">{listDisplayName}</span>
      {accountEmail ? <span className="text-murmur"> in {accountEmail}</span> : null}.
    </p>
  );
}

/**
 * A hairline row per list rather than a card each — four identical cards make every
 * list feel equally consequential when only one of them is a choice being made.
 */
function ListRow({
  list,
  isCurrent,
  isBusy,
  onChoose,
}: {
  list: TodoListOption;
  isCurrent: boolean;
  isBusy: boolean;
  onChoose: () => void;
}) {
  return (
    <li className="flex items-baseline gap-4 py-3 border-b border-mineral">
      <span className="text-ink">{list.displayName}</span>
      {list.isFlaggedEmailList ? (
        <span className="text-sm text-murmur">Where your flagged mail already goes</span>
      ) : null}
      {list.isShared ? <span className="text-sm text-murmur">Shared</span> : null}
      <span className="ml-auto">
        {isCurrent ? (
          <span className="text-sm text-murmur">In use</span>
        ) : (
          <Button variant="ghost" size="sm" disabled={isBusy} onClick={onChoose}>
            Use this list
          </Button>
        )}
      </span>
    </li>
  );
}

/**
 * The reconnect case is the one worth wording carefully. Nothing has broken and no
 * mail is affected — Microsoft simply never granted access to tasks, and it will not
 * ask again on its own.
 */
function Unavailable({
  reason,
}: {
  reason: 'reconnect-required' | 'no-microsoft-account' | 'provider-unavailable';
}) {
  if (reason === 'no-microsoft-account') {
    return (
      <p className="text-sm text-murmur max-w-measure">
        This needs a Microsoft account. Connect one under Accounts and your lists will show up here.
      </p>
    );
  }

  if (reason === 'reconnect-required') {
    return (
      <p className="text-sm text-ink max-w-measure">
        Your Microsoft account was connected before Inbox Clinic asked to see your tasks. Your mail
        is unaffected. Reconnect the account under Accounts and your lists will show up here.
      </p>
    );
  }

  return (
    <p className="text-sm text-murmur max-w-measure">
      Microsoft did not answer just now. Nothing has changed. Come back whenever.
    </p>
  );
}
